import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useData } from '../contexts/DataContext';
import { Search, ExternalLink, FileText, Globe, ArrowUp, ArrowDown, Check, Loader2 } from 'lucide-react';

// Type declaration for gtag
declare global {
  interface Window {
    gtag?: (command: string, action: string, params: any) => void;
  }
}

interface LinkSuggestion {
  id: string;
  title: string;
  slug: string;
  type: 'blog' | 'page';
  excerpt?: string | null;
  categories?: string[];
  tags?: string[];
  publishedAt?: string | null;
  relevanceScore: number;
}

interface EnhancedLinkEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onLinkInsert: (url: string, text: string) => void;
  initialText?: string;
  initialUrl?: string;
}

const EnhancedLinkEditor: React.FC<EnhancedLinkEditorProps> = ({
  isOpen,
  onClose,
  onLinkInsert,
  initialText = '',
  initialUrl = ''
}) => {
  const { blogPosts, pages } = useData();
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'external' | 'internal'>('external');
  const [error, setError] = useState('');
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Generate all suggestions from blog posts and pages
  const allSuggestions: LinkSuggestion[] = useMemo(() => {
    const suggestions: LinkSuggestion[] = [];
    
    // Add blog posts
    blogPosts
      .filter(post => post.status === 'published')
      .forEach(post => {
        suggestions.push({
          id: post.id,
          title: post.title,
          slug: post.slug,
          type: 'blog',
          excerpt: post.excerpt || null,
          categories: post.categories || [],
          tags: post.tags || [],
          publishedAt: post.publishedAt || null,
          relevanceScore: 0
        });
      });
    
    // Add pages
    pages
      .filter(page => page.status === 'published')
      .forEach(page => {
        suggestions.push({
          id: page.id,
          title: page.title,
          slug: page.slug,
          type: 'page',
          excerpt: null,
          categories: [],
          tags: [],
          publishedAt: null,
          relevanceScore: 0
        });
      });
    
    return suggestions;
  }, [blogPosts, pages]);

  // Filter and rank suggestions based on search term
  const filteredSuggestions = useMemo(() => {
    if (!searchTerm.trim()) {
      return allSuggestions.slice(0, 10); // Show first 10 if no search
    }

    const searchLower = searchTerm.toLowerCase();
    
    return allSuggestions
      .map(suggestion => {
        let score = 0;
        const titleLower = suggestion.title.toLowerCase();
        const excerptLower = suggestion.excerpt?.toLowerCase() || '';
        
        // Title exact match gets highest score
        if (titleLower === searchLower) score += 100;
        // Title starts with search term
        else if (titleLower.startsWith(searchLower)) score += 50;
        // Title contains search term
        else if (titleLower.includes(searchLower)) score += 30;
        
        // Excerpt contains search term
        if (excerptLower.includes(searchLower)) score += 10;
        
        // Category/tag matches
        if (suggestion.categories?.some(cat => cat.toLowerCase().includes(searchLower))) score += 20;
        if (suggestion.tags?.some(tag => tag.toLowerCase().includes(searchLower))) score += 15;
        
        // Recent posts get slight boost
        if (suggestion.publishedAt) {
          const daysSincePublished = (Date.now() - new Date(suggestion.publishedAt).getTime()) / (1000 * 60 * 60 * 24);
          if (daysSincePublished < 30) score += 5;
        }
        
        return { ...suggestion, relevanceScore: score };
      })
      .filter(suggestion => suggestion.relevanceScore > 0)
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 8); // Limit to 8 suggestions
  }, [allSuggestions, searchTerm]);

  // Track link insertion analytics
  const trackLinkInsertion = useCallback((url: string, text: string, type: 'external' | 'internal') => {
    try {
      // Track link insertion for analytics
      if (typeof window !== 'undefined' && 'gtag' in window && typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'link_inserted', {
          event_category: 'editor',
          event_label: type,
          value: 1
        });
      }
      
      // Log for debugging
      console.log('Link inserted:', { url, text, type });
    } catch (error) {
      console.warn('Failed to track link insertion:', error);
    }
  }, []);

  // Handle suggestion selection
  const handleSuggestionClick = useCallback((suggestion: LinkSuggestion) => {
    try {
      const slug = `/${suggestion.slug}`;
      setUrl(slug);
      setText(suggestion.title);
      setShowSuggestions(false);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Error selecting suggestion:', error);
      setError('Failed to select suggestion. Please try again.');
    }
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (filteredSuggestions[selectedIndex]) {
          const suggestion = filteredSuggestions[selectedIndex];
          const slug = `/${suggestion.slug}`;
          setUrl(slug);
          setText(suggestion.title);
          setShowSuggestions(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setShowSuggestions(false);
        break;
    }
  }, [showSuggestions, filteredSuggestions, selectedIndex]);

  // Validate URL
  const validateUrl = useCallback((url: string): boolean => {
    if (activeTab === 'internal') {
      return url.trim().length > 0;
    }
    
    try {
      // For external URLs, ensure they have a protocol
      const urlToTest = url.startsWith('http') ? url : `https://${url}`;
      new URL(urlToTest);
      return true;
    } catch {
      return false;
    }
  }, [activeTab]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    setError('');
    
    if (!url.trim() || !text.trim()) {
      setError('Please provide both URL and link text');
      return;
    }

    if (!validateUrl(url)) {
      setError(activeTab === 'external' ? 'Please enter a valid URL' : 'Please enter a valid page/post URL');
      return;
    }

    setIsLoading(true);
    
    try {
      // Track the link insertion
      trackLinkInsertion(url.trim(), text.trim(), activeTab);
      
      // Insert the link asynchronously without any navigation
      await new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          try {
            // Ensure we're not causing any navigation
            const finalUrl = url.trim();
            const finalText = text.trim();
            
            // Call the link insertion callback
            onLinkInsert(finalUrl, finalText);
            
            resolve();
          } catch (error) {
            console.error('Link insertion error:', error);
            reject(error);
          }
        }, 50); // Reduced delay for better responsiveness
      });
      
      // Close modal only after successful insertion
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      setError('Failed to insert link. Please try again.');
      console.error('Link insertion error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [url, text, activeTab, validateUrl, trackLinkInsertion, onLinkInsert, onClose]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
      setText(initialText);
      setSearchTerm('');
      setSelectedIndex(0);
      setShowSuggestions(false);
      setActiveTab('external');
      setError('');
      
      // Focus search input after a short delay
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, initialUrl, initialText]);

  // Show suggestions when typing in internal tab
  useEffect(() => {
    if (activeTab === 'internal' && searchTerm.trim()) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [activeTab, searchTerm]);

  // Auto-fill text when URL is entered
  useEffect(() => {
    if (activeTab === 'internal' && url && !text) {
      const suggestion = allSuggestions.find(s => {
        const slug = `/${s.slug}`;
        return slug === url;
      });
      if (suggestion) {
        setText(suggestion.title);
      }
    }
  }, [url, text, activeTab, allSuggestions]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add Link</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Tab Navigation */}
          <div className="flex space-x-1 mb-6">
            <button
              onClick={() => setActiveTab('external')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'external'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ExternalLink size={16} />
              <span>External Link</span>
            </button>
            <button
              onClick={() => setActiveTab('internal')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'internal'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText size={16} />
              <span>Internal Page/Post</span>
              {allSuggestions.length > 0 && (
                <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full">
                  {allSuggestions.length}
                </span>
              )}
            </button>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            {/* URL Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {activeTab === 'external' ? 'URL' : 'Page/Post URL'}
              </label>
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={activeTab === 'internal' ? searchTerm : url}
                  onChange={(e) => {
                    if (activeTab === 'internal') {
                      setSearchTerm(e.target.value);
                    } else {
                      setUrl(e.target.value);
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder={
                    activeTab === 'external' 
                      ? 'https://example.com' 
                      : 'Search pages and posts...'
                  }
                />
                {activeTab === 'internal' && (
                  <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                {error}
              </div>
            )}

            {/* Suggestions for Internal Tab */}
            {activeTab === 'internal' && showSuggestions && filteredSuggestions.length > 0 && (
              <div 
                ref={suggestionsRef}
                className="border border-gray-200 rounded-lg bg-white shadow-lg max-h-64 overflow-y-auto"
              >
                {filteredSuggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      index === selectedIndex ? 'bg-amber-50 border-r-2 border-amber-500' : ''
                    } ${index === 0 ? 'rounded-t-lg' : ''} ${index === filteredSuggestions.length - 1 ? 'rounded-b-lg' : ''}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {suggestion.type === 'blog' ? (
                          <FileText className="h-4 w-4 text-amber-600" />
                        ) : (
                          <Globe className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {suggestion.title}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            suggestion.type === 'blog' 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {suggestion.type}
                          </span>
                        </div>
                        {suggestion.excerpt && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {suggestion.excerpt}
                          </p>
                        )}
                        {suggestion.categories && suggestion.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {suggestion.categories.slice(0, 3).map(category => (
                              <span key={category} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {category}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {index === selectedIndex && (
                        <Check className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No suggestions message */}
            {activeTab === 'internal' && showSuggestions && searchTerm.trim() && filteredSuggestions.length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">
                No pages or posts found matching "{searchTerm}"
              </div>
            )}

            {/* Link Text Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link Text (optional)
              </label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Text to display for the link"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit();
                }}
                disabled={!url.trim() || !text.trim() || isLoading}
                className="flex-1 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : (
                  <span>Add Link</span>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnhancedLinkEditor; 